import { setupKeyboard } from './input/keyboard.js';
import { setupTouchControls } from './input/joystick.js';
import { audio } from './lib/audio.js';
import { HAPTIC, vibrate } from './lib/haptic.js';
import { loadAssets } from './render/assets.js';
import { getCameraOffset, resetCamera, updateCamera } from './render/camera.js';
import { drawVisibilityMask, initRenderer, renderFrame } from './render/renderer.js';
import { getShakeOffset, shake, updateShake } from './render/shake.js';
import { createStage, resizeViewport, type Stage } from './render/stage.js';
import { buildStaticLayers } from './render/static.js';
import { triggerAbility, updateAbilities } from './systems/abilities.js';
import { updateCameras } from './systems/cameras.js';
import { updateGuards } from './systems/guards.js';
import {
  spawnFirework,
  spawnFootstep,
  spawnParticle,
  updateParticles,
} from './systems/particles.js';
import { updatePlayer } from './systems/player.js';
import {
  computeRank,
  formatTime,
  RANK_COLORS,
  recordEvent,
  saveBestIfBetter,
} from './systems/score.js';
import { setKoTotal, updateBest, updateHUD } from './ui/hud.js';
import { buildMenu, type MenuApi } from './ui/menu.js';
import { hideToast, setupToastClick, showToast } from './ui/toast.js';
import { getDefaultLevel, getLevel, type LevelDef } from './world/levels.js';
import type { AbilityName, GuardState, World } from './world/types.js';
import { STATE } from './world/types.js';
import { createWorld, resetWorld } from './world/world.js';

const STEP_VELOCITY_THRESHOLD = 12; // px/s — below this, the player is "still"
const FOOTSTEP_PERIOD = 0.28;
// Period (s) between dust-mote spawn rounds — every round, every light
// gets a chance to emit one. Tuned so motes in flight stay around 25-35.
const DUST_PERIOD = 0.7;

async function main(): Promise<void> {
  const host = document.getElementById('stage');
  if (!host) throw new Error('#stage element missing');

  // Preload PNG assets in parallel with creating the placeholder world.
  // Without this, the first level rendering would fall back to the asset
  // textures while still loading and flash blank.
  await loadAssets();

  // Boot Pixi against a placeholder world so we can show the menu first.
  // The real world is (re)built when a level is selected.
  let world: World = createWorld(getDefaultLevel());
  const stage = await createStage(world, host);
  let staticBuilt = false;

  let prevStates = new Map<number, GuardState>();
  let wasMoving = false;
  let footstepTimer = 0;
  let dustTimer = 0;
  let triedFullscreen = false;

  let menu: MenuApi;

  // Debug hook — exposed when ?debug=1 so e2e tests can read & poke world state.
  const debugMode = location.search.includes('debug=1');
  interface DebugApi {
    world: World | null;
    audio: typeof audio;
    selectLevel: (id: string) => void;
  }
  let debugApi: DebugApi | null = null;
  if (debugMode) {
    debugApi = {
      world: null,
      audio,
      selectLevel: (id: string) => {
        const lvl = getLevel(id) ?? getDefaultLevel();
        startLevel(lvl);
      },
    };
    (window as unknown as { __game: DebugApi }).__game = debugApi;
  }

  function onWin(): void {
    if (world.state !== STATE.PLAY) return;
    world.state = STATE.WIN;
    audio.stopStep();
    audio.play('win');

    const result = computeRank(world);
    const isNewBest = saveBestIfBetter(result, world.levelId);
    updateBest(world.levelId);

    // Win ceremony — golden firework + haptic on top ranks.
    if (result.rank === 'S' || result.rank === 'A') {
      spawnFirework(world, world.player.x, world.player.y);
      vibrate(HAPTIC.win);
    }

    const breakdown =
      `<b>${result.label}</b> · ` +
      `Détecté: ${result.detected ? '✗' : '✓'} · ` +
      `K.O.: ${result.kos}/${result.totalGuards} · ` +
      `Temps: ${formatTime(result.time)}` +
      (isNewBest ? ' · ★ NEW BEST' : '') +
      ' · R pour rejouer · Esc pour menu';
    showToast(`Rang ${result.rank}`, breakdown, RANK_COLORS[result.rank]);
  }

  function onLose(): void {
    if (world.state !== STATE.PLAY) return;
    world.state = STATE.LOSE;
    audio.stopStep();
    audio.play('capture');
    shake(18);
    vibrate(HAPTIC.capture);
    showToast('✗ REPÉRÉ — MISSION COMPROMISE', 'R pour réessayer · Esc pour menu', '#ff4a5a');
  }

  function onReset(): void {
    resetWorld(world);
    prevStates.clear();
    wasMoving = false;
    footstepTimer = 0;
    audio.stopStep();
    hideToast();
  }

  function startLevel(level: LevelDef): void {
    // Build a fresh world for the chosen level and reuse the existing stage.
    world = createWorld(level);

    // Resize the visible window if this level's bounds differ from the
    // previous one (e.g. Entrepôt 18×13 → Labo 30×19).
    resizeViewport(stage, world);

    // Wipe every layer before rebuilding — both the static (floor /
    // walls / lights) AND the dynamic ones, so static sprites parked
    // in dynamic layers (exit pad, intel) by buildStaticLayers don't
    // accumulate across level switches.
    for (const c of Object.values(stage.layers)) {
      c.removeChildren();
    }
    buildStaticLayers(stage, world);
    initRenderer(stage);
    staticBuilt = true;

    // Snap the camera onto the player so the level fades in already framed.
    resetCamera();

    setKoTotal(world);
    updateBest(world.levelId);
    prevStates = new Map();
    wasMoving = false;
    footstepTimer = 0;
    audio.stopStep();
    hideToast();
    menu.hide();

    // Hide ability buttons that aren't relevant to this level. Saves
    // mobile screen real estate and reduces confusion.
    const hackBtn = document.querySelector<HTMLDivElement>('.ab[data-ab="hack"]');
    if (hackBtn) hackBtn.style.display = world.cameras.length > 0 ? '' : 'none';

    // Toggle fog-of-war masking. Always wipe the previous level's mask
    // geometry so a switch fog → non-fog doesn't leak shapes.
    stage.fogMask.clear();
    stage.worldRoot.mask = level.fogOfWar ? stage.fogMask : null;

    // Note: audio.unlock() is intentionally NOT called here. The browser
    // requires a user gesture to start the AudioContext — input handlers
    // (keyboard.ts, joystick.ts, menu card click) take care of that.

    if (debugApi) debugApi.world = world;
  }

  function backToMenu(): void {
    audio.stopStep();
    hideToast();
    menu.refresh();
    menu.show();
  }

  function onAbility(name: AbilityName): void {
    audio.unlock();
    maybeRequestFullscreen();
    if (triggerAbility(world, name)) {
      // Haptic feedback for the impactful actions only.
      if (name === 'takedown') vibrate(HAPTIC.ko);
    }
  }

  function onMuteToggle(): void {
    audio.unlock();
    audio.toggleMute();
  }

  function maybeRequestFullscreen(): void {
    if (triedFullscreen) return;
    triedFullscreen = true;
    // Mobile only — desktop fullscreen is annoying.
    if (!document.body.classList.contains('show-mobile')) return;
    const el = document.documentElement;
    try {
      if (el.requestFullscreen) void el.requestFullscreen();
    } catch {
      // ignore — some browsers reject without a user gesture or in iframes.
    }
  }

  // === Menu wiring ===
  menu = buildMenu({
    onLevelSelect: (id) => {
      const lvl = getLevel(id) ?? getDefaultLevel();
      startLevel(lvl);
    },
  });

  // === Input ===
  setupKeyboard({
    onAbility,
    onReset,
    onMuteToggle,
    onBackToMenu: backToMenu,
  });
  setupTouchControls({ onAbility, onReset });
  setupToastClick(onReset);

  // HUD menu button
  const menuBtn = document.getElementById('menuBtn');
  menuBtn?.addEventListener('click', backToMenu);

  // Re-fit the viewport on window resize / orientation change. The
  // mobile-vs-desktop viewport cap depends on the window width and
  // pointer kind, so a phone rotated to landscape gets a wider cap.
  addEventListener('resize', () => resizeViewport(stage, world));
  addEventListener('orientationchange', () => resizeViewport(stage, world));

  // === Boot: ?level=<id> skips the menu (handy for tests + ngrok shares) ===
  const urlLevel = new URLSearchParams(location.search).get('level');
  const initial = urlLevel ? getLevel(urlLevel) : null;
  if (initial) {
    startLevel(initial);
  } else {
    menu.show();
  }

  // === Game loop ===
  stage.app.ticker.add((ticker) => {
    const dt = Math.min(0.05, ticker.deltaMS / 1000);

    // Don't run the simulation while the menu is up. We still tick the
    // shake so any in-flight tremor decays back to zero before the next
    // session, and we still apply the (zero) offset so the stage stays
    // pinned at origin when the menu is showing.
    if (document.body.classList.contains('show-menu') || !staticBuilt) {
      updateShake(dt);
      stage.app.stage.position.set(0, 0);
      return;
    }

    if (world.state === STATE.PLAY) {
      world.time += dt;
      updatePlayer(world, dt, onWin);
      updateAbilities(world, dt);

      // Cameras detect first — if any of them sees the player, we yank
      // every alive guard into chase pointing at the player's current
      // position. Guards keep their normal perception logic afterwards
      // so a successful escape (LoS break + box) eventually decays the
      // alarm.
      const alarm = updateCameras(world, dt);
      if (alarm) {
        for (const g of world.guards) {
          if (g.ko) continue;
          g.alert = 100;
          g.lastSeen = { x: world.player.x, y: world.player.y };
          g.target = { x: world.player.x, y: world.player.y };
          g.state = 'chase';
        }
      }

      updateGuards(world, dt, onLose);

      // Footstep audio + dust on the same cadence.
      const speed = Math.hypot(world.player.vx, world.player.vy);
      const isMoving = !world.player.inBox && speed > STEP_VELOCITY_THRESHOLD;
      if (isMoving && !wasMoving) audio.startStep();
      else if (!isMoving && wasMoving) audio.stopStep();
      wasMoving = isMoving;

      if (isMoving) {
        footstepTimer -= dt;
        if (footstepTimer <= 0) {
          footstepTimer = FOOTSTEP_PERIOD;
          spawnFootstep(world, world.player.x, world.player.y, world.player.dir);
        }
      } else {
        footstepTimer = 0;
      }

      // Dust motes drifting in lit zones — each light emits a soft warm
      // particle every DUST_PERIOD with some probability. Reuses the
      // particle system, additive blend already enabled.
      dustTimer -= dt;
      if (dustTimer <= 0) {
        dustTimer = DUST_PERIOD;
        for (const L of world.lights) {
          if (Math.random() > 0.2) {
            const a = Math.random() * Math.PI * 2;
            // sqrt → uniform area sampling instead of clustering at center
            const r = Math.sqrt(Math.random()) * L.r * 0.85;
            spawnParticle(world, {
              x: L.x + Math.cos(a) * r,
              y: L.y + Math.sin(a) * r,
              vx: (Math.random() - 0.5) * 3,
              vy: 5 + Math.random() * 5,
              life: 3 + Math.random() * 2,
              color: 0xffe6c0,
              size: 0.6 + Math.random() * 0.8,
              drag: 0.3,
              shape: 'circle',
            });
          }
        }
      }

      updateParticles(world, dt);

      // Light flicker — tiny per-frame alpha modulation on every bloom
      // sprite, with each light getting a different phase (i*1.7) so
      // the room doesn't feel synchronised.
      for (let i = 0; i < stage.lightFlickers.length; i++) {
        const phase = i * 1.7;
        const flicker =
          0.92 +
          0.05 * Math.sin(world.time * 3 + phase) +
          0.03 * Math.sin(world.time * 13 + phase * 0.7);
        stage.lightFlickers[i]!.alpha = 0.22 * flicker;
      }

      // Guard state transitions → audio + score + haptic + shake.
      for (const g of world.guards) {
        if (g.ko) continue;
        const prev = prevStates.get(g.id);
        if (prev !== g.state) {
          if (g.state === 'chase' && prev !== 'chase') {
            audio.play('detect');
            recordEvent(world, 'detected');
            shake(7);
            vibrate(HAPTIC.detect);
          } else if (
            (g.state === 'suspicious' || g.state === 'investigate') &&
            prev !== 'suspicious' &&
            prev !== 'investigate' &&
            prev !== 'chase'
          ) {
            audio.play('suspicious');
          }
          prevStates.set(g.id, g.state);
        }
      }
    } else {
      // Win/lose state — still tick particles so the firework / dust plays out.
      updateParticles(world, dt);
    }

    // Camera + shake → stage offset. Round the FINAL position to whole
    // pixels — sub-pixel stage offsets make every sprite sample its
    // texture between texels, and the linear filter blends a slightly
    // different texel pair each frame as the camera lerps, producing
    // a visible edge shimmer.
    updateCamera(world, stage, dt);
    updateShake(dt);
    const cam = getCameraOffset(stage);
    const shk = getShakeOffset();
    const stageX = Math.round(cam.x + shk.x);
    const stageY = Math.round(cam.y + shk.y);
    stage.app.stage.position.set(stageX, stageY);
    // Cancel the stage transform on the HUD overlay so anything we
    // draw in it sits in screen coords regardless of where the camera
    // is looking. Saves doing the math at every draw call site.
    stage.hudOverlay.position.set(-stageX, -stageY);

    // Fog of war — re-draw the visibility mask each frame in world coords.
    // Pixi inherits the camera+shake transform automatically.
    if (stage.worldRoot.mask) {
      drawVisibilityMask(stage.fogMask, world);
    }

    renderFrame(stage, world);
    updateHUD(world);
    // Exit compass — only meaningful on fog-of-war levels where the
    // pad is invisible until the player is right on top of it.
    drawExitCompass(stage, world);
  });
}

/**
 * Draw a chevron at the screen edge pointing toward the exit pad,
 * but only on fog-of-war levels and only while the pad is offscreen
 * or off-vision. Gives the player a sense of direction in the dark
 * without revealing the rest of the level.
 */
function drawExitCompass(stage: Stage, world: World): void {
  const g = stage.hudOverlay;
  g.clear();
  if (!stage.worldRoot.mask) return; // not a fog level

  const ex = world.exitPos.x;
  const ey = world.exitPos.y;
  const px = world.player.x;
  const py = world.player.y;

  // If the player is already close to the pad, don't bother — they
  // can see it through the fog disc.
  const distToExit = Math.hypot(ex - px, ey - py);
  if (distToExit < 140) return;

  // Compute screen-space position of the exit using the same transform
  // app.stage applies to world content (camera offset + viewport).
  const stageX = stage.app.stage.position.x;
  const stageY = stage.app.stage.position.y;
  const sx = ex + stageX;
  const sy = ey + stageY;

  // Project to the viewport edge along the player→exit direction.
  // Player is roughly at the viewport centre, so we draw a chevron at
  // the closest edge along that direction.
  const cx = stage.viewportW / 2;
  const cy = stage.viewportH / 2;
  const margin = 28;
  const ang = Math.atan2(sy - cy, sx - cx);
  // Distance from centre to viewport edge along ang.
  const halfW = cx - margin;
  const halfH = cy - margin;
  // Find scale t such that (cx + t*cos(ang), cy + t*sin(ang)) hits the
  // viewport rect with margin. tx and ty are the per-axis distances.
  const ca = Math.cos(ang);
  const sa = Math.sin(ang);
  const tx = ca === 0 ? Infinity : Math.abs(halfW / ca);
  const ty = sa === 0 ? Infinity : Math.abs(halfH / sa);
  const t = Math.min(tx, ty);
  // If the exit is already inside the visible rect, skip the chevron.
  const exitInsideViewport =
    sx > margin && sx < stage.viewportW - margin && sy > margin && sy < stage.viewportH - margin;
  if (exitInsideViewport) return;

  const ax = cx + ca * t;
  const ay = cy + sa * t;

  // Pulse to draw the eye.
  const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 280);
  const accent = 0x4afca0;

  // Soft halo behind the chevron.
  g.circle(ax, ay, 18 + pulse * 3).fill({ color: accent, alpha: 0.12 });

  // Chevron — three points making an arrow head pointing along ang.
  const tipR = 12;
  const baseR = 7;
  const half = 0.5; // arrow half-angle (rad)
  const tipX = ax + ca * tipR;
  const tipY = ay + sa * tipR;
  const leftX = ax + Math.cos(ang + Math.PI - half) * baseR;
  const leftY = ay + Math.sin(ang + Math.PI - half) * baseR;
  const rightX = ax + Math.cos(ang + Math.PI + half) * baseR;
  const rightY = ay + Math.sin(ang + Math.PI + half) * baseR;
  g.poly([tipX, tipY, leftX, leftY, rightX, rightY]).fill({
    color: accent,
    alpha: 0.85 + 0.15 * pulse,
  });
  g.poly([tipX, tipY, leftX, leftY, rightX, rightY]).stroke({
    color: 0xffffff,
    alpha: 0.6,
    width: 1,
  });
}

void main().catch((err) => {
  console.error('Boot failed:', err);
});
